/**
 * ImageUpload - Multi-Image Upload Component
 * 
 * Why this matters: Provides an intuitive drag-and-drop interface for users
 * to upload multiple images of their citrus trees for comprehensive diagnosis.
 * 
 * Features:
 * - Drag and drop support
 * - Click to upload
 * - Preview thumbnails with labels
 * - Remove individual images
 * - Max 4 images limit
 */

import React, { useCallback, useState, useEffect } from 'react';
import { Upload, X, Camera, Leaf, TreeDeciduous, Apple } from 'lucide-react';

export interface UploadedImage {
  id: string;
  file: File;
  base64Data: string;
  mimeType: string;
  label: string;
  previewUrl: string;
}

interface ImageUploadProps {
  images: UploadedImage[];
  onImagesChange: (images: UploadedImage[]) => void;
  maxImages?: number;
  disabled?: boolean;
}

/**
 * Image upload configuration constants
 * Why this matters: Centralizes magic numbers for easy maintenance and clarity
 */
const IMAGE_UPLOAD_CONFIG = {
  MAX_SIZE_MB: 10,
  MAX_SIZE_BYTES: 10 * 1024 * 1024,
  MAX_IMAGES_DEFAULT: 4,
  ACCEPTED_TYPES: ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif']
} as const;

// Suggested image labels
const IMAGE_LABELS = [
  { id: 'leaves', label: 'Leaves', icon: Leaf, description: 'Close-up of affected leaves' },
  { id: 'trunk', label: 'Trunk/Stem', icon: TreeDeciduous, description: 'Bark and stem condition' },
  { id: 'whole', label: 'Whole Tree', icon: TreeDeciduous, description: 'Overall tree view' },
  { id: 'fruit', label: 'Fruit', icon: Apple, description: 'Any fruit or flowers' }
];

const ImageUpload: React.FC<ImageUploadProps> = ({
  images,
  onImagesChange,
  maxImages = IMAGE_UPLOAD_CONFIG.MAX_IMAGES_DEFAULT,
  disabled = false
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  /**
   * Cleanup object URLs on unmount to prevent memory leaks
   * Why this matters: blob: URLs hold references to file data until explicitly revoked
   */
  useEffect(() => {
    return () => {
      images.forEach(img => {
        if (img.previewUrl.startsWith('blob:')) {
          URL.revokeObjectURL(img.previewUrl);
        }
      });
    };
  }, [images]);

  /**
   * Convert file to base64 and create preview
   * Why this matters: Backend expects base64 data for Gemini API
   */
  const processFile = useCallback(async (file: File): Promise<UploadedImage | null> => {
    // Validate file type using centralized config
    if (!IMAGE_UPLOAD_CONFIG.ACCEPTED_TYPES.includes(file.type as typeof IMAGE_UPLOAD_CONFIG.ACCEPTED_TYPES[number])) {
      setUploadError(`Invalid file type: ${file.type}. Please use JPEG, PNG, or WebP.`);
      return null;
    }

    // Validate file size using centralized config
    if (file.size > IMAGE_UPLOAD_CONFIG.MAX_SIZE_BYTES) {
      setUploadError(`File too large. Maximum size is ${IMAGE_UPLOAD_CONFIG.MAX_SIZE_MB}MB.`);
      return null;
    }

    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        // Extract base64 data (remove data:image/xxx;base64, prefix)
        const base64Data = result.split(',')[1];
        
        // Determine suggested label based on index
        const usedLabels = images.map(img => img.label);
        const suggestedLabel = IMAGE_LABELS.find(l => !usedLabels.includes(l.label))?.label || 'Additional';
        
        resolve({
          id: `img_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
          file,
          base64Data,
          mimeType: file.type,
          label: suggestedLabel,
          previewUrl: result
        });
      };
      reader.onerror = () => {
        setUploadError('Failed to read file.');
        resolve(null);
      };
      reader.readAsDataURL(file);
    });
  }, [images]);

  /**
   * Handle file selection from input or drop
   */
  const handleFiles = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    
    setUploadError(null);
    
    const remainingSlots = maxImages - images.length;
    if (remainingSlots <= 0) {
      setUploadError(`Maximum ${maxImages} images allowed.`);
      return;
    }

    const filesToProcess = Array.from(files).slice(0, remainingSlots);
    const processedImages: UploadedImage[] = [];

    for (const file of filesToProcess) {
      const processed = await processFile(file);
      if (processed) {
        processedImages.push(processed);
      }
    }

    if (processedImages.length > 0) {
      onImagesChange([...images, ...processedImages]);
    }
  }, [images, maxImages, onImagesChange, processFile]);

  /**
   * Handle drag events
   */
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragOver(true);
    }
  }, [disabled]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    
    if (!disabled) {
      handleFiles(e.dataTransfer.files);
    }
  }, [disabled, handleFiles]);

  /**
   * Handle file input change
   */
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
    // Reset input so same file can be selected again
    e.target.value = '';
  }, [handleFiles]);

  /**
   * Remove an image
   */
  const handleRemoveImage = useCallback((imageId: string) => {
    onImagesChange(images.filter(img => img.id !== imageId));
  }, [images, onImagesChange]);

  /**
   * Update image label
   */
  const handleLabelChange = useCallback((imageId: string, newLabel: string) => {
    onImagesChange(images.map(img => 
      img.id === imageId ? { ...img, label: newLabel } : img
    ));
  }, [images, onImagesChange]);

  const canAddMore = images.length < maxImages;

  return (
    <div className="doctor-image-upload">
      {/* Upload Zone */}
      {canAddMore && (
        <div
          className={`doctor-upload-zone ${isDragOver ? 'drag-over' : ''} ${disabled ? 'disabled' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <input
            type="file"
            id="image-upload"
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
            multiple
            onChange={handleInputChange}
            disabled={disabled}
            className="doctor-upload-input"
          />
          <label htmlFor="image-upload" className="doctor-upload-label">
            <div className="doctor-upload-content">
              <div className="doctor-upload-icon">
                <Camera size={32} />
              </div>
              <div className="doctor-upload-text">
                <span className="doctor-upload-title">
                  {isDragOver ? 'Drop images here' : 'Upload photos of your citrus tree'}
                </span>
                <span className="doctor-upload-subtitle">
                  Drag & drop or click to browse • JPEG, PNG, WebP
                </span>
              </div>
            </div>
          </label>
        </div>
      )}

      {/* Suggested Shots */}
      {images.length === 0 && (
        <div className="doctor-upload-suggestions">
          <h4 className="doctor-suggestions-title">For best results, include photos of:</h4>
          <div className="doctor-suggestions-grid">
            {IMAGE_LABELS.map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.id} className="doctor-suggestion-item">
                  <Icon size={20} className="doctor-suggestion-icon" />
                  <span className="doctor-suggestion-label">{item.label}</span>
                  <span className="doctor-suggestion-desc">{item.description}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Error Message */}
      {uploadError && (
        <div className="doctor-upload-error">
          {uploadError}
        </div>
      )}

      {/* Image Previews */}
      {images.length > 0 && (
        <div className="doctor-image-previews">
          <div className="doctor-previews-grid">
            {images.map((image) => (
              <div key={image.id} className="doctor-preview-card">
                <div className="doctor-preview-image-container">
                  <img 
                    src={image.previewUrl} 
                    alt={image.label}
                    className="doctor-preview-image"
                  />
                  <button
                    className="doctor-preview-remove"
                    onClick={() => handleRemoveImage(image.id)}
                    disabled={disabled}
                    aria-label="Remove image"
                  >
                    <X size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
